/**
 * /rate — "Rate my auction": paste a Sleeper league, the whole room gets
 * graded against the real-cell benchmarks.
 *
 * Standalone surface like /submit and /scout: no builder, no hook chain, no
 * local league state. Mobile-first, same one-column shell as /submit, and it
 * borrows submitStyles so the two funnel pages read as one family.
 *
 * Deep-linkable: /rate?league=<id> re-fetches and re-renders live on load.
 * Leagues are public on Sleeper's API, so the link IS the share object; no
 * graded room is ever stored ON A SERVER.
 *
 * WHAT IS STORED IS STORED IN THIS BROWSER AND NOWHERE ELSE: which tab was last
 * open (below), the roster the team tab was filling in (rateTeamStore.js), and
 * since Sep 2 2026 the whole rooms this browser has graded, so that rating a
 * second league no longer costs you the first (rateSavedRooms.js — it also
 * explains why all three are separate keys). Every one of them is guarded, and
 * none of them leaves the device.
 *
 * 🔴 THIS PAGE NO LONGER POINTS ANYWHERE ELSE (Sep 3 2026). It used to carry
 * "Not on Sleeper? Send your results here" under the league form, handing a
 * Yahoo or ESPN room off to /submit. It does not need to: the paste box above
 * that form reads exactly those rooms, and every room this page grades joins the
 * data on its own without being asked. So /rate has no cross-link to /submit on
 * any surface. The /submit PAGE stays where it is - the trends emails link
 * straight to it - and this file's remaining mentions of it are prose about
 * shared styles, not routes out.
 *
 * TWO TABS, SubmitPage's pattern. "Rate my league" is the default and should
 * stay that way: it is the path that needs nothing but a paste and its data
 * joins the published numbers directly, where the team tab costs a person a
 * roster's worth of typing. The team tab exists for the rooms the league tab
 * cannot reach at all - Yahoo, ESPN, and anyone who just wants their own squad
 * graded - and it grades against the corpus rather than against a room.
 *
 * A ?league= deep link ALWAYS lands on the league tab: the link is the share
 * object for a graded room, so following one must show that room and not a
 * blank form.
 *
 * 🔴 WHAT AN ARRIVAL AT /rate RESTORES, IN ORDER, AND WHAT IT NO LONGER DOES
 * (Sep 3 2026). Three keys are read on mount and every one of them is this
 * browser's own: auctionArchitect_rateTab (which tab), auctionArchitect_ratePaste
 * (a pasted room and the screen it was on), and auctionArchitect_rateTeam:<slot>
 * (the rosters). A fourth, auctionArchitect_rateSavedRooms, is read for the
 * picker and has never restored anything by itself. Until today the paste
 * record ALSO decided the screen: a live one put the reader straight back
 * inside their last room, which is why a league can appear "already there" on
 * an arrival nobody expected - a browser that still holds that key is not a
 * fresh browser, and none of these keys is shared between browsers, profiles or
 * private sessions. Now the paste record restores the room's CONTENT but never
 * the reader's position: an arrival lands on this front door, with the room one
 * tap away in the picker (graded) or in the resume card (not graded yet).
 */

import { useEffect, useRef, useState } from "react";
import { theme } from "../../data/theme.js";
import { captureLeagueRated, captureRateSleeperGraded } from "../../utils/rateAnalytics.js";
import { captureSleeperLeagueCaptured } from "../../utils/recapAnalytics";
import { readPaste, readTab, writeTab } from "../../utils/rateTeamStore.js";
import { readSavedRooms, removeSavedRoom } from "../../utils/rateSavedRooms.js";
import { C, SZ, card, input, label, primaryBtn, fineText, errorText, sectionTitle } from "../Submit/submitStyles.js";
import DraftPasteBox from "./DraftPasteBox.jsx";
import ReportView from "./ReportView.jsx";
import SavedRoomsPicker from "./SavedRoomsPicker.jsx";
import TeamTab from "./TeamTab.jsx";

const TABS = [
  { key: "league", label: "Rate my league" },
  { key: "team", label: "Rate my team" },
];

/**
 * The funnel column: a form, a paste box, one team's report.
 *
 * 560 on /submit, 640 here, and both are sized for ONE reading column of body
 * copy. Nothing about that is wrong for the front door.
 */
export const RATE_COLUMN = 640;

/**
 * The league report's column.
 *
 * 🔴 A ROOM IS NOT A FORM AND IT WAS BEING DRAWN IN A FORM'S COLUMN. Ben, on
 * the deployed page: the seat strip wraps two cramped cards per row. It does —
 * ten chips carrying a badge, a name, a build, a surplus and a spend bar do not
 * fit two-up in 604px of content, so the one control whose job is comparing
 * seats was the hardest thing on the page to compare with.
 *
 * 1100 rather than the builder's 1400: it is the widest column the site already
 * uses for a REPORT (`.almanac-hero` in recap.css), and 1400 would stretch a
 * team card's one-line facts — a 44px badge, a name and a sentence — across a
 * void. At 1100 the seat grid lands four clean columns and the room cards keep
 * a readable line length.
 *
 * Mobile is untouched by construction: this is a max, and every viewport under
 * it was already at 100%.
 */
export const RATE_REPORT_COLUMN = 1100;

/**
 * A pasted room that is still mid-flight, or null.
 *
 * 🔴 THE RESUME CARD EXISTS SO THAT "LAND ON THE FRONT DOOR" COSTS NOBODY A
 * LEAGUE. A room that reached its grade files itself in rateSavedRooms and
 * comes back through the picker, so it needs nothing here. A paste still on the
 * confirm screen has NOT been filed anywhere, and dropping the reader on the
 * landing with no offer to go back would be the "if I click away I lose the
 * league" bug wearing a different hat. Steps room and report are past the
 * grade, so they are the picker's job and not this card's.
 */
function pendingResume() {
  const p = readPaste();
  return p && p.step !== "room" && p.step !== "report" ? p : null;
}

export default function RatePage() {
  // The league tab is the default and stays it; a stored value only overrides
  // it for somebody who has already been here and chosen the other one. A
  // ?league= deep link still forces the league tab in the effect below, because
  // the link is the share object for a graded room.
  const [tab, setTab] = useState(() => readTab() || "league");
  const [value, setValue] = useState("");
  // idle | loading | error | done — one machine, same pattern as SleeperForm.
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const requested = useRef(false);
  // 🔴 A PASTED DRAFT IS A WHOLE LEAGUE, SO IT BELONGS ON THIS TAB, AND IT IS
  // GRADED BY THE OTHER ONE. Ben, Sep 1 2026: "We have to add this to the rate
  // my league." He is right about where people will look for it - a Yahoo or
  // ESPN room is a league, and the league tab is the tab that says so. But the
  // machinery that grades a room nobody fetched from Sleeper already exists and
  // is the team tab's (LocalRoomReport, the room strip, the corpus submit
  // card). Building a second copy of it here would be two report surfaces
  // drifting apart. So the box lives here and hands the parsed room across.
  const [pasted, setPasted] = useState(null);
  // 🔴 A PASTED LEAGUE BELONGS TO THE LEAGUE TAB, AND STAYS THERE. Ben, Sep 1
  // 2026: "When I am seeing my whole league the rate my team tab is
  // highlighted. If I click away I lose the league." Both were the same
  // mistake: the paste was handed to the team tab, which highlighted the wrong
  // tab and then had no idea it was holding a league when the tab changed. The
  // room machinery still lives in TeamTab - one implementation, not two - but
  // the LEAGUE tab is where it renders when the room came from a paste.
  //
  // 🔴 IT STARTS FALSE, SO AN ARRIVAL IS ALWAYS THE FRONT DOOR (Sep 3 2026).
  // Ben, prod: a league was on screen in a session he expected to be empty.
  // The cause was this initialiser reading the paste record and teleporting
  // straight into the room, which also made the tab above a dead control -
  // there was no way back out that was not "Start over". A returning reader
  // chooses to re-enter their room; they are not put in it. The record itself
  // is untouched: the picker and the resume card are how it reopens.
  const [roomLive, setRoomLive] = useState(false);
  // 🔴 THE LEAGUES THIS BROWSER HAS ALREADY GRADED. Read once on mount and
  // re-read whenever the page comes back to the front door, which is the only
  // moment the list can have changed: a room files itself on grading, so the
  // walk out of a room is exactly when a new chip should be waiting.
  const [savedRooms, setSavedRooms] = useState(() => readSavedRooms());
  // A room handed to TeamTab to reopen. Same one-shot handoff as `pasted`.
  const [reopen, setReopen] = useState(null);
  // 🔴 WHETHER A ROOM IS ON SCREEN, WHICH ONLY TeamTab KNOWS. The page shell
  // owns the column width — one wrapper, one place — but the thing that decides
  // it is TeamTab's own step: a room report and a seat report carrying the room
  // switcher are league-scale, a settings form and a roster ladder are not.
  // Reported up rather than re-derived here, because re-deriving it would mean
  // this file keeping a second copy of TeamTab's step machine.
  const [roomView, setRoomView] = useState(false);
  // An un-graded pasted room waiting to be picked back up. See pendingResume.
  const [resume, setResume] = useState(() => pendingResume());
  // Which grade is the current one. Bumped by goHome so a fetch the reader
  // walked away from cannot land a report on top of the front door.
  const gen = useRef(0);

  const busy = phase === "loading";
  const canSubmit = !busy && value.trim().length > 0;

  async function grade(rawInput) {
    const myGen = (gen.current += 1);
    setPhase("loading");
    setResult(null);
    try {
      const resp = await fetch("/api/rate-league", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: rawInput }),
      });
      const data = await resp.json().catch(() => ({}));
      // Somebody tapped the tab while this was in flight. Answering them with
      // the room they just left is the exact teleport this change removes.
      if (myGen !== gen.current) return;
      if (!resp.ok || !data.ok) {
        setResult({ error: data.error || "Something went wrong. Try again in a moment.", code: data.code });
        setPhase("error");
        return;
      }
      setResult(data);
      setPhase("done");
      // The deep link is the growth loop: once a room is graded, the address
      // bar carries the shareable form of this exact page.
      try {
        window.history.replaceState(null, "", `/rate?league=${data.leagueId}`);
      } catch {
        /* ignore */
      }
      const r = data.report;
      captureLeagueRated({
        format: r.format,
        teams: r.teams,
        budget: r.budget,
        keeperFlag: r.keeperFlag,
        roomHeat: r.roomHeat,
      });
      /* 🔴 THE FUNNEL EVENT ALONGSIDE THE SURFACE EVENT, NOT INSTEAD OF IT.
         `league_rated` is the shipped series about what kind of rooms this
         reaches (budget, keepers, heat); this is the funnel step "a Sleeper
         league got through the fetch", which is the thing the paste and manual
         paths have their own step for. Two questions, two events, and the
         existing one is not redefined to answer the new one. */
      captureRateSleeperGraded({ teams: r.teams, format: r.format });
      /* 🔴 THE HARVEST LIST IS BUILT FROM sleeper_league_captured, AND /rate WAS
         NOT ON IT. The id list behind the corpus comes from that event plus
         sleeper_user_looked_up (the SQL of record is
         docs/data/harvest-league-ids-aug26.md), and until now only RecapImport
         and SettingsModal fired either. So a pasted or hand-entered room joined
         the corpus through auto-submit while a graded SLEEPER league — the
         richest source there is, because the whole draft is fetchable from the
         id alone — was thrown away at the end of the request that found it.

         🔴 TWO MODULES, OPPOSITE CONTRACTS, AND THIS LINE USES EACH FOR WHAT IT
         IS. rateAnalytics carries no id, ever; recapAnalytics carries the RAW
         Sleeper id as its one documented exception, because the point of that
         event is being able to go back to Sleeper's public API. Neither is bent
         to do the other's job — `rate_sleeper_graded` above is unchanged and
         still carries no id, and rateAnalytics.test.js asserts that it does not.

         Nothing new leaves the browser: this same raw id is already in the
         /rate?league=<id> URL written two lines up, which PostHog records with
         the pageview. `source` keeps rate-originated ids separable from imports
         without the harvest SQL changing at all.

         previousLeagueId is passed straight through and is currently undefined:
         /api/rate-league does not forward Sleeper's `previous_league_id`. It is
         read here rather than fetched, per the rule on the capture function —
         never worth an extra request — so if the endpoint ever forwards it, the
         chain to prior seasons starts working with no change on this side. */
      captureSleeperLeagueCaptured({
        leagueId: data.leagueId,
        leagueName: data.leagueName,
        season: data.season,
        source: "rate_grade",
        previousLeagueId: data.previousLeagueId,
      });
    } catch {
      if (myGen !== gen.current) return;
      setResult({ error: "Could not reach the server. Check your connection and try again." });
      setPhase("error");
    }
  }

  /**
   * The way home, from a room that arrived any way at all.
   *
   * 🔴 IT NAVIGATES AND IT NEVER DELETES. Ben, prod Sep 3 2026: the "Rate my
   * league" control at the top should put you back on the front door. Inside a
   * room it used to do nothing whatever - the tab was already selected - so the
   * only exits were "Start over" and "Rate another league", both of which throw
   * something away. This throws away nothing: a graded room stays in "Your
   * rated leagues", the in-progress paste record stays exactly where it is, and
   * the rosters stay in their slot. It moves the reader, not their data.
   *
   * The saved list and the resume offer are re-read here rather than trusted
   * from mount, because walking out of a room is precisely the moment a new
   * chip should be waiting.
   */
  function goHome() {
    gen.current += 1;
    setRoomLive(false);
    setPasted(null);
    setReopen(null);
    setTab("league");
    // A fetched league's report is a room too, and its ?league= address would
    // re-grade it on the next reload.
    if (phase !== "idle") {
      setPhase("idle");
      setResult(null);
      setValue("");
    }
    try {
      window.history.replaceState(null, "", "/rate");
    } catch {
      /* ignore */
    }
    setSavedRooms(readSavedRooms());
    setResume(pendingResume());
  }

  // ?league=<id> grades on load, once. The ref guards StrictMode's double
  // effect run from firing two POSTs and two analytics events.
  useEffect(() => {
    if (requested.current) return;
    const id = new URLSearchParams(window.location.search).get("league");
    if (id) {
      requested.current = true;
      setTab("league");
      setValue(id);
      grade(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remembered so a person who came for the team tab does not land on the
  // league form every time. Written on change rather than on unmount: this page
  // is usually left by closing the tab, and an unmount handler would not run.
  useEffect(() => {
    writeTab(tab);
  }, [tab]);

  // A live pasted league owns the page. Somebody whose last visit ended on the
  // team tab must not return to a team form with their league invisible behind
  // a tab that is no longer on screen.
  useEffect(() => {
    if (roomLive) setTab("league");
    else {
      setSavedRooms(readSavedRooms());
      setResume(pendingResume());
    }
  }, [roomLive]);

  const showForm = phase !== "done";

  // A fetched Sleeper league is a room too, and it needs the same column the
  // pasted one gets. Both signals in one place so the page cannot be wide on
  // one path and narrow on the other.
  const wideColumn = roomView || phase === "done";

  const tabStyle = (active) => ({
    flex: "1 1 auto",
    padding: "10px 14px",
    fontSize: SZ.small,
    fontWeight: 700,
    lineHeight: 1.3,
    borderRadius: theme.radius.lg,
    border: `1px solid ${active ? C.border.brand : C.border.default}`,
    background: active ? C.bg.raised : "transparent",
    color: active ? C.text.primary : C.text.muted,
    cursor: "pointer",
  });

  return (
    <div style={{ minHeight: "100vh", background: C.bg.app, color: C.text.primary, fontFamily: theme.fonts.body }}>
      <div
        data-testid="rate-column"
        data-wide={wideColumn ? "1" : "0"}
        style={{
          maxWidth: wideColumn ? RATE_REPORT_COLUMN : RATE_COLUMN,
          margin: "0 auto",
          padding: "32px 18px 64px",
        }}
      >
        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontSize: SZ.hero,
            fontWeight: 900,
            lineHeight: 1.15,
            margin: 0,
            // The title alone is centred. The paragraph under it and the tab
            // pair stay left, because a centred block of body copy is harder to
            // read and the tabs are a control rather than a heading.
            textAlign: "center",
            background: C.brand.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Rate my auction
        </h1>
        <p style={{ fontSize: SZ.body, color: C.text.secondary, margin: "10px 0 18px", lineHeight: 1.5 }}>
          {tab === "league"
            ? roomLive
              ? "Your league, every team graded against every other. Open any seat for that manager's own report."
              : "Paste your draft results, or a Sleeper league, and every team in the room gets a grade, a best buy, and a worst overpay, measured against what real 2026 auction rooms paid."
            : "Not on Sleeper, or just want your own squad looked at? Fill in your roster slot by slot and it gets measured against real 2026 auction teams."}
        </p>

        {/* 🔴 WITH A LEAGUE OPEN THERE IS NO SECOND TAB TO GO TO. Ben, Sep 1
            2026: "If you are doing rate my league, you shouldn't be able to get
            to rate my team because it's already built in to rate my league."
            Right - inside a pasted league, every team's own report is one tap
            from the room, so the other tab is not a second feature, it is a way
            to walk out of the league you just built and land somewhere that
            looks like it lost it. The tabs come back when the league is
            cleared, and "Start over" inside the room is the way to clear it. */}
        <div
          role="tablist"
          style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}
        >
          {(roomLive ? TABS.filter((t) => t.key === "league") : TABS).map((t) => (
            <button
              key={t.key}
              role="tab"
              data-testid={"rate-tab-" + t.key}
              aria-selected={tab === t.key}
              /* 🔴 THE LEAGUE TAB IS A WAY HOME, NOT A NO-OP. From inside a
                 room it is the only tab on screen and it was already selected,
                 so a tap did nothing at all. It now lands on this page's front
                 door - paste box, saved-league picker and all - without
                 clearing one byte of what is on it. */
              onClick={() => (t.key === "league" ? goHome() : setTab("team"))}
              title={t.key === "league" && roomLive ? "Back to your leagues" : undefined}
              style={tabStyle(tab === t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* One TeamTab, rendered under whichever tab owns the room right now.
            A pasted league renders under "Rate my league"; anything typed by
            hand renders under "Rate my team", exactly as before. */}
        {(tab === "team" || (tab === "league" && roomLive)) && (
          <TeamTab
            incomingPaste={pasted}
            incomingRoom={reopen}
            onOpenLeague={() => setTab("league")}
            onPasteApplied={() => setPasted(null)}
            onRoomApplied={() => setReopen(null)}
            onRoomCleared={() => setRoomLive(false)}
            onRoomView={setRoomView}
            ignorePaste={!roomLive}
          />
        )}

        {/* 🔴 THE ROOMS ALREADY GRADED SIT ABOVE THE WAY TO GRADE A NEW ONE.
            Somebody returning to this page is far more often coming back to a
            league they already rated than pasting their fourth draft, and a
            picker underneath a paste box and a Sleeper form is a picker nobody
            scrolls to. Absent entirely when nothing is saved, so a first visit
            is exactly the page it was.

            Opening one goes STRAIGHT to that room: no re-confirm, no second
            look at the receipt, and no corpus post — a room read back out of
            storage is not a new grade. TeamTab's restoredId is what enforces
            the last one. */}
        {/* The room somebody was in the middle of, offered rather than forced.
            Above the picker because it is the more recent thing and the one
            that is not saved anywhere else. */}
        {tab === "league" && !roomLive && showForm && resume && (
          <div style={{ ...card, marginBottom: 12 }} data-testid="resume-room">
            <div
              style={{
                fontSize: SZ.micro,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.text.faint,
                marginBottom: 8,
              }}
            >
              Still open
            </div>
            <button
              type="button"
              data-testid="resume-room-open"
              onClick={() => setRoomLive(true)}
              style={{ ...primaryBtn(true), width: "100%" }}
            >
              Pick up the league you were rating
            </button>
            <div style={fineText}>
              Read off the draft you pasted and not graded yet. It is on this device only.
            </div>
          </div>
        )}

        {tab === "league" && !roomLive && showForm && (
          <SavedRoomsPicker
            rooms={savedRooms}
            onOpen={(room) => {
              setReopen(room);
              setRoomLive(true);
            }}
            onRemove={(id) => setSavedRooms(removeSavedRoom(id))}
          />
        )}

        {tab === "league" && !roomLive && showForm && (
          <DraftPasteBox
            onFill={(read, text) => {
              setPasted({ read, text });
              setRoomLive(true);
            }}
          />
        )}

        {tab === "league" && !roomLive && showForm && (
          <div style={card}>
            {/* The third section gets a heading of its own weight. It had a form
                label and nothing else, so the block a Sleeper user came for read
                as a footnote to the paste box above it. */}
            <div data-testid="sleeper-form-title" style={sectionTitle}>
              Rate a Sleeper league
            </div>
            <label htmlFor="rate-league-input" style={label}>
              Sleeper league URL or league ID
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <input
                id="rate-league-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canSubmit && grade(value.trim())}
                disabled={busy}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="sleeper.com/leagues/… or 1180389865513353216"
                style={{ ...input, flex: "1 1 220px", width: "auto" }}
              />
              <button
                onClick={() => canSubmit && grade(value.trim())}
                disabled={!canSubmit}
                style={{ ...primaryBtn(canSubmit), flex: "1 1 120px" }}
              >
                {busy ? "Grading…" : "Rate my Auction League"}
              </button>
            </div>
            {busy && (
              <div style={{ marginTop: 10, fontSize: SZ.small, color: C.text.secondary }}>
                Fetching your draft and grading every pick…
              </div>
            )}
            {phase === "error" && (
              <div style={errorText}>
                {result?.error}
                {result?.code === "not_complete" && (
                  <>
                    {" "}
                    <a href="/" style={{ color: C.brand.accent, fontWeight: 700 }}>
                      Open the mock room
                    </a>
                  </>
                )}
              </div>
            )}
            <div style={fineText}>
              Completed drafts are public on Sleeper's API. Nothing is stored, and rated leagues join
              the dataset behind the auction trends.
            </div>
          </div>
        )}

        {tab === "league" && !roomLive && phase === "done" && result && (
          <ReportView data={result} onReset={goHome} />
        )}
      </div>
    </div>
  );
}
